import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Mic, Square, Loader2, RotateCcw, X, PhoneOff, Volume2 } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { useApi, OFFICER_IMG } from "@/lib/api";
import Transcript from "@/components/Transcript";
import { Switch } from "@/components/ui/switch";

const SILENCE_MS = 2000;
const MIN_SPEECH_MS = 500;
const pickMime = () => ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"].find((m) => window.MediaRecorder?.isTypeSupported?.(m)) || "";
const PHASE_LABEL = { idle: "Waiting", speaking: "Officer is speaking", ready: "Your turn — press the mic to answer", recording: "Recording your answer...", listening: "Listening — start speaking whenever you are ready", processing: "Officer is reviewing your answer...", ended: "Interview concluded" };

function useElapsed(startedAt) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  const s = startedAt ? Math.max(0, Math.floor((now - new Date(startedAt).getTime()) / 1000)) : 0;
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}

export default function InterviewRoom() {
  const { id } = useParams();
  const api = useApi();
  const navigate = useNavigate();
  const location = useLocation();
  const [session, setSession] = useState(null);
  const [phase, setPhase] = useState("idle");
  const [level, setLevel] = useState(0);
  const [heard, setHeard] = useState(false);
  const [silence, setSilence] = useState(0);
  const [handsFree, setHandsFree] = useState(() => localStorage.getItem("handsFree") !== "off");
  const audioRef = useRef(new Audio());
  const lastAudio = useRef(location.state?.opening?.audio_b64 || "");
  const endRef = useRef(false);
  const recRef = useRef(null);
  const discardRef = useRef(false);
  const rafRef = useRef(0);
  const endedRef = useRef(false);
  const elapsed = useElapsed(session?.started_at);

  const killRec = () => {
    if (recRef.current?.state === "recording") { discardRef.current = true; recRef.current.stop(); }
  };

  const finish = useCallback(async () => {
    endedRef.current = true;
    killRec();
    setPhase("ended");
    try {
      await api("post", `/sessions/${id}/end`);
      navigate(`/sessions/${id}`);
    } catch {
      endedRef.current = false;
      toast.error("Could not end the interview. Please try again.");
      setPhase("ready");
    }
  }, [api, id, navigate]);

  const play = useCallback((b64) => {
    if (!b64) { if (endRef.current) finish(); else setPhase("ready"); return; }
    lastAudio.current = b64;
    const a = audioRef.current;
    a.src = `data:audio/mp3;base64,${b64}`;
    a.onended = () => (endRef.current ? finish() : setPhase("ready"));
    setPhase("speaking");
    a.play().catch(() => setPhase("idle"));
  }, [finish]);

  useEffect(() => {
    api("get", `/sessions/${id}`).then((s) => {
      if (s.status !== "in_progress") return navigate(`/sessions/${id}`, { replace: true });
      setSession(s);
      if (location.state?.opening) play(location.state.opening.audio_b64);
    }).catch(() => navigate("/dashboard"));
    const a = audioRef.current;
    return () => { endedRef.current = true; a.pause(); killRec(); clearInterval(rafRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const replay = async () => {
    if (!lastAudio.current) {
      const last = [...(session?.turns || [])].reverse().find((t) => t.speaker === "officer");
      if (!last) return;
      setPhase("processing");
      lastAudio.current = (await api("post", "/tts", { text: last.text })).audio_b64;
    }
    play(lastAudio.current);
  };

  const submit = async (blob, mime) => {
    if (endedRef.current) return;
    setPhase("processing");
    const ext = mime.includes("mp4") ? "mp4" : mime.includes("ogg") ? "ogg" : "webm";
    const fd = new FormData();
    fd.append("audio", new File([blob], `answer.${ext}`, { type: mime.split(";")[0] || "audio/webm" }));
    try {
      const res = await api("post", `/sessions/${id}/answer`, fd);
      setSession((s) => ({ ...s, turns: [...s.turns, res.user_turn, res.officer.turn] }));
      endRef.current = res.officer.end;
      play(res.officer.audio_b64);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not process your answer. Please try again.");
      setPhase("ready");
    }
  };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = pickMime();
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      const chunks = [];
      rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      rec.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        clearInterval(rafRef.current);
        ctx.close();
        setLevel(0);
        setSilence(0);
        if (discardRef.current) { discardRef.current = false; if (!endedRef.current) setPhase("ready"); return; }
        submit(new Blob(chunks, { type: rec.mimeType || mime }), rec.mimeType || mime || "audio/webm");
      };
      const ctx = new AudioContext();
      const an = ctx.createAnalyser();
      ctx.createMediaStreamSource(stream).connect(an);
      an.fftSize = 1024;
      const buf = new Uint8Array(an.fftSize);
      const t0 = performance.now();
      let floor = 0.01, last = t0, speechMs = 0, quietSince = null, spoke = false;
      setHeard(false);
      const tick = () => {
        an.getByteTimeDomainData(buf);
        const rms = Math.sqrt(buf.reduce((a, v) => a + ((v - 128) / 128) ** 2, 0) / buf.length);
        const now = performance.now(), dt = now - last;
        last = now;
        if (now - t0 < 400) floor = Math.min(0.04, Math.max(floor, rms));
        const loud = rms > Math.max(0.03, floor * 2.5);
        setLevel(Math.min(1, rms * 6));
        if (loud) { speechMs += dt; quietSince = null; if (!spoke && speechMs >= MIN_SPEECH_MS) { spoke = true; setHeard(true); } }
        else if (spoke) quietSince = quietSince ?? now;
        const q = quietSince ? now - quietSince : 0;
        setSilence(spoke ? Math.min(1, q / SILENCE_MS) : 0);
        if (autoStopRef.current && spoke && q >= SILENCE_MS && rec.state === "recording") { clearInterval(rafRef.current); rec.stop(); }
      };
      rafRef.current = setInterval(tick, 50);
      rec.start();
      recRef.current = rec;
      setPhase("recording");
    } catch {
      setHandsFree(false);
      toast.error("Microphone access is required");
    }
  };

  const autoStopRef = useRef(handsFree);
  useEffect(() => { autoStopRef.current = handsFree; localStorage.setItem("handsFree", handsFree ? "on" : "off"); }, [handsFree]);
  useEffect(() => {
    if (phase !== "ready" || !handsFree || endedRef.current) return;
    const t = setTimeout(startRec, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, handsFree]);

  const stopRec = (discard = false) => { discardRef.current = discard; recRef.current?.stop(); };
  const officerQ = [...(session?.turns || [])].reverse().find((t) => t.speaker === "officer");
  const speaking = phase === "speaking";

  return (
    <div className="max-w-6xl mx-auto grid lg:grid-cols-12 gap-8" data-testid="interview-room">
      <section className="lg:col-span-7 bg-slate-900 border border-slate-800 p-8 lg:p-12 flex flex-col items-center relative">
        <div className="w-full flex items-center justify-between font-mono text-xs text-slate-400 mb-10">
          <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse" /> LIVE · DESK 04</span>
          <span data-testid="interview-timer">{elapsed}</span>
        </div>

        <div className="relative w-48 h-48 flex items-center justify-center">
          <span aria-hidden="true" className={`absolute inset-0 rounded-full border-2 border-blue-500/60 transition-transform duration-300 ${speaking ? "scale-110 animate-ping" : "scale-100 opacity-30"}`} />
          <span aria-hidden="true" className={`absolute -inset-3 rounded-full border border-blue-500/20 ${speaking ? "animate-pulse" : ""}`} />
          <img src={OFFICER_IMG} alt="Officer Fredo Quist" className="w-44 h-44 rounded-full object-cover object-top grayscale relative z-10" data-testid="officer-avatar" />
        </div>
        <p className="font-cabinet font-bold text-white text-2xl mt-6">Officer Fredo Quist</p>
        <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-slate-500 mt-1" data-testid="interview-phase">{PHASE_LABEL[(phase === "recording" && !heard) || (phase === "ready" && handsFree) ? "listening" : phase]}</p>
        <label className="mt-4 flex items-center gap-2 font-mono text-[11px] text-slate-400 cursor-pointer">
          <Switch checked={handsFree} onCheckedChange={setHandsFree} data-testid="hands-free-toggle" /> HANDS-FREE (auto-listen & submit on pause)
        </label>

        <div className="w-full mt-8 min-h-[96px] bg-slate-950 border border-slate-800 p-5">
          <p className="font-mono text-[10px] tracking-[0.2em] text-slate-600 mb-2">OFFICER</p>
          <p className="font-mono text-sm text-slate-200 leading-relaxed" data-testid="current-officer-question">{officerQ?.text || "..."}</p>
        </div>

        <div className="mt-10 flex flex-col items-center gap-4">
          {phase === "recording" ? (
            <button onClick={() => stopRec(false)} data-testid="stop-recording-btn"
              className="w-24 h-24 rounded-full bg-rose-600 hover:bg-rose-500 text-white flex items-center justify-center transition-transform"
              style={{ transform: `scale(${1 + Math.min(level, 1) * 0.25})`, boxShadow: `0 0 ${20 + level * 60}px rgba(244,63,94,0.5)` }}>
              <Square className="w-8 h-8" />
            </button>
          ) : (
            <button onClick={startRec} disabled={phase !== "ready"} data-testid="start-recording-btn"
              className="w-24 h-24 rounded-full bg-blue-600 hover:bg-blue-500 disabled:bg-slate-800 disabled:text-slate-600 text-white flex items-center justify-center transition-colors shadow-[0_0_40px_rgba(37,99,235,0.35)]">
              {phase === "processing" ? <Loader2 className="w-8 h-8 animate-spin" /> : <Mic className="w-8 h-8" />}
            </button>
          )}
          <div className="flex items-center gap-3 h-9">
            {phase === "recording" && heard && silence > 0 && (
              <span className="font-mono text-[11px] text-amber-400 flex items-center gap-2" data-testid="silence-indicator">
                <span className="w-16 h-1 bg-slate-800"><span className="block h-full bg-amber-400" style={{ width: `${silence * 100}%` }} /></span> submitting on pause
              </span>
            )}
            {phase === "recording" && (
              <button onClick={() => stopRec(true)} data-testid="discard-recording-btn" className="flex items-center gap-1 text-xs text-slate-400 hover:text-white px-3 py-2 border border-slate-800 transition-colors"><X className="w-3 h-3" /> Discard & re-record</button>
            )}
            {(phase === "ready" || phase === "idle") && (
              <button onClick={replay} data-testid="replay-officer-btn" className="flex items-center gap-1 text-xs text-slate-400 hover:text-white px-3 py-2 border border-slate-800 transition-colors">
                {phase === "idle" ? <Volume2 className="w-3 h-3" /> : <RotateCcw className="w-3 h-3" />} {phase === "idle" ? "Hear the officer" : "Replay question"}
              </button>
            )}
          </div>
        </div>

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button disabled={phase === "processing" || phase === "ended"} data-testid="end-interview-btn" className="mt-10 flex items-center gap-2 text-xs font-mono tracking-wider text-rose-400 hover:text-rose-300 disabled:opacity-40 transition-colors">
              <PhoneOff className="w-4 h-4" /> END INTERVIEW
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent className="bg-slate-900 border-slate-800">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-cabinet text-white">End the interview now?</AlertDialogTitle>
              <AlertDialogDescription>Your answers so far will be submitted for assessment.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="end-interview-cancel">Continue interview</AlertDialogCancel>
              <AlertDialogAction data-testid="end-interview-confirm" onClick={() => { audioRef.current.pause(); finish(); }} className="bg-rose-600 hover:bg-rose-500">End & submit</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </section>

      <section className="lg:col-span-5 bg-slate-900 border border-slate-800 flex flex-col max-h-[calc(100vh-10rem)]">
        <div className="p-5 border-b border-slate-800 flex justify-between">
          <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-slate-500">Live transcript</p>
          <p className="font-mono text-[10px] text-slate-600">{session?.destination} · {session?.team_role_label}</p>
        </div>
        <Transcript turns={session?.turns || []} autoScroll />
      </section>
    </div>
  );
}
